import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Layers,
  CreditCard,
  DollarSign,
  BarChart3,
  Activity,
  HardDrive,
  Server,
  Mic,
  FileText,
  Shield,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const { user, logout } = useAuth();

  const menuItems = [
    { to: '/dashboard', label: 'Dashboard Geral', icon: LayoutDashboard },
    { to: '/clientes', label: 'Clientes', icon: Users },
    { to: '/planos', label: 'Planos', icon: Layers },
    { to: '/assinaturas', label: 'Assinaturas', icon: CreditCard },
    { to: '/pagamentos', label: 'Pagamentos', icon: DollarSign },
    { to: '/utilizacao', label: 'Utilização', icon: BarChart3 },
    { to: '/processamentos', label: 'Processamentos', icon: Activity },
    { to: '/transferencias', label: 'Transferências Drive', icon: HardDrive },
    { to: '/servidores', label: 'Servidores', icon: Server },
    { to: '/vozes', label: 'Vozes Piper', icon: Mic },
    { to: '/relatorios', label: 'Relatórios', icon: FileText },
    { to: '/administradores', label: 'Administradores', icon: Shield },
    { to: '/configuracoes', label: 'Configurações', icon: Settings },
  ];

  return (
    <aside
      className={`fixed top-0 left-0 z-30 h-screen bg-surface border-r border-bdr transition-all duration-300 flex flex-col justify-between ${
        collapsed ? 'w-20' : 'w-64'
      }`}
    >
      {/* Brand Header */}
      <div>
        <div className="h-20 flex items-center justify-between px-4 border-b border-bdr">
          {!collapsed ? (
            <div className="flex items-center gap-2 overflow-hidden">
              <img
                src="/logo-edurender.png"
                alt="EduRender"
                className="h-9 w-auto object-contain"
              />
              <span className="text-[10px] font-bold bg-brand-graphite text-white px-1.5 py-0.5 rounded tracking-wider">
                MASTER
              </span>
            </div>
          ) : (
            <div className="w-full flex justify-center">
              <img
                src="/symbol-edurender.png"
                alt="EduRender"
                className="h-9 w-9 object-contain"
              />
            </div>
          )}

          <button
            onClick={onToggle}
            className="p-1.5 rounded-md hover:bg-surface-secondary text-txt-secondary hover:text-txt-primary transition-colors"
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="p-3 space-y-0.5 overflow-y-auto max-h-[calc(100vh-160px)]">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    isActive
                      ? 'bg-brand-light text-brand-primary border-l-4 border-brand-primary shadow-sm font-semibold'
                      : 'text-txt-primary hover:bg-surface-secondary hover:text-txt-primary'
                  } ${collapsed ? 'justify-center px-0' : ''}`
                }
                title={collapsed ? item.label : undefined}
              >
                <Icon size={18} className="shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Footer User Info */}
      <div className="p-3 border-t border-bdr bg-surface-secondary">
        {!collapsed ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5 overflow-hidden">
              <div className="w-8 h-8 rounded-full bg-brand-graphite text-white flex items-center justify-center font-bold text-xs shrink-0">
                {user?.fullName?.charAt(0) || 'A'}
              </div>
              <div className="truncate">
                <p className="text-xs font-semibold text-txt-primary truncate">
                  {user?.fullName || 'Super Admin'}
                </p>
                <p className="text-[10px] text-brand-primary font-medium truncate uppercase">
                  {user?.role || 'super_admin'}
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="p-1.5 text-txt-muted hover:text-status-error rounded-md transition-colors"
              title="Sair da conta"
            >
              <LogOut size={16} />
            </button>
          </div>
        ) : (
          <div className="flex justify-center">
            <button
              onClick={logout}
              className="p-2 text-txt-muted hover:text-status-error rounded-md transition-colors"
              title="Sair da conta"
            >
              <LogOut size={18} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};
